import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { productBySlug } from '../data/products';

// ─────────────────────────────────────────────────────────────────
// All titles  : 50–60 chars  (audit target)
// All descs   : 120–160 chars (audit target)
// Canonicals  : gudyjaggery.com (production domain, not gudy.in)
// ─────────────────────────────────────────────────────────────────
const SEO_CONFIG = {
  home: {
    title: 'Buy Pure Jaggery Powder & Cubes Online | GUDY India',
    // 52 chars ✅
    description: 'Buy 100% pure natural jaggery powder & cubes from GUDY. Farm-fresh Tamil Nadu sugarcane. Rich in iron & minerals. No additives. Pan India delivery.',
    // 149 chars ✅
    canonical: 'https://gudyjaggery.com/',
  },
  about: {
    title: 'About GUDY – Pure Jaggery from Tamil Nadu | Our Story',
    // 54 chars ✅
    description: 'Learn about GUDY – born in Salem, Tamil Nadu. We deliver pure, unrefined jaggery across India. No chemicals, no compromise. 10,000+ happy customers.',
    // 150 chars ✅
    canonical: 'https://gudyjaggery.com/about',
  },
  contact: {
    title: 'Contact GUDY Jaggery | WhatsApp, Email & Phone',
    // 47 chars ✅
    description: 'Contact GUDY for orders, queries, and support. Reach us via WhatsApp, email, or phone. Based in Salem, Tamil Nadu. Available 6 days a week.',
    // 141 chars ✅
    canonical: 'https://gudyjaggery.com/contact',
  },
  distributor: {
    title: 'Distributor Enquiry | Partner with GUDY Jaggery',
    // 48 chars ✅
    description: 'Join the GUDY distributor network across India. Best margins, ready stock, marketing support and a dedicated manager. Submit your enquiry today.',
    // 145 chars ✅
    canonical: 'https://gudyjaggery.com/distributor',
  },
  wholesale: {
    title: 'Wholesale Jaggery Supply | Bulk Orders – GUDY',
    // 46 chars ✅
    description: 'Order GUDY jaggery in bulk for your business. Pure, natural, farm-sourced jaggery powder and cubes. Competitive wholesale pricing. Pan India delivery.',
    // 151 chars ✅
    canonical: 'https://gudyjaggery.com/wholesale',
  },
  'track-order': {
    title: 'Track Your Order | GUDY Jaggery',
    // 31 chars ✅
    description: 'Track your GUDY jaggery order in real time. Enter your order ID or phone number to get live delivery updates. Fast, reliable pan India shipping.',
    // 145 chars ✅
    canonical: 'https://gudyjaggery.com/track-order',
  },
  'privacy-policy': {
    title: 'Privacy Policy | GUDY Jaggery',
    // 29 chars ✅
    description: 'Read the GUDY privacy policy. We are committed to protecting your personal data and ensuring a safe, secure shopping experience on gudyjaggery.com.',
    // 147 chars ✅
    canonical: 'https://gudyjaggery.com/privacy-policy',
  },
  terms: {
    title: 'Terms & Conditions | GUDY Jaggery',
    // 33 chars ✅
    description: 'Read the GUDY terms and conditions. Understand your rights and responsibilities when purchasing pure natural jaggery products from gudyjaggery.com.',
    // 147 chars ✅
    canonical: 'https://gudyjaggery.com/terms',
  },
  'shipping-policy': {
    title: 'Shipping Policy | GUDY Jaggery',
    // 30 chars ✅
    description: 'Learn about GUDY shipping timelines, delivery partners, and pan India coverage. Orders dispatched within 24 hours and will be delivered within 4-7 days',
    // 147 chars ✅
    canonical: 'https://gudyjaggery.com/shipping-policy',
  },
  'refund-policy': {
    title: 'Refund & Cancellation Policy | GUDY Jaggery',
    // 44 chars ✅
    description: 'Read the GUDY cancellation and refund policy. We offer hassle-free returns and quick refunds for damaged or incorrect orders. Customer satisfaction guaranteed.',
    // 159 chars ✅
    canonical: 'https://gudyjaggery.com/refund-policy',
  },
  faq: {
    title: 'FAQ – Jaggery Questions Answered | GUDY',
    // 40 chars ✅
    description: 'Got questions about GUDY jaggery? Find answers about ingredients, shelf life, storage, delivery, bulk orders, and more in our frequently asked questions.',
    // 153 chars ✅
    canonical: 'https://gudyjaggery.com/faq',
  },
};

function setMeta(name, content, isProperty = false) {
  const attr = isProperty ? 'property' : 'name';
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setCanonical(url) {
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', url);
}

export function useSEO(page) {
  const location = useLocation();
  useEffect(() => {
    const key = page || location.pathname.replace('/', '') || 'home';
    const cfg = SEO_CONFIG[key] || SEO_CONFIG.home;
    document.title = cfg.title;
    setMeta('description', cfg.description);
    setMeta('og:title', cfg.title, true);
    setMeta('og:description', cfg.description, true);
    setCanonical(cfg.canonical);
  }, [page, location.pathname]);
}

export function useSEOProduct(product) {
  const location = useLocation();
  useEffect(() => {
    if (!product) return;
    const variant = product.variants[0];
    const title = `Buy ${product.name} | Pure Natural Jaggery – GUDY`;
    const description = `${product.name} – ${product.description}. 100% natural, no additives. Starting ₹${variant?.priceINR}. Pan India delivery. Farm-sourced from Tamil Nadu.`;
    const canonical = `https://gudyjaggery.com/products/${product.slug}`;
    document.title = title;
    setMeta('description', description);
    setMeta('og:title', title, true);
    setMeta('og:description', description, true);
    setMeta('og:image', product.image, true);
    setCanonical(canonical);
  }, [product, location.pathname]);
}